'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        noClose?: boolean;
        fullScreen?: boolean;
        manualMaxWidth?: boolean;
        theme?: 'light' | 'dark';
        overlayClassName?: string;
        // 'default' slides + zooms in, which fights for CPU on content-heavy dialogs (large tables,
        //  maps) and reads as janky. 'fade' drops the transform animation and just fades.
        animation?: 'default' | 'fade';
    }
>(
    (
        {
            className,
            children,
            noClose,
            fullScreen,
            manualMaxWidth,
            theme,
            overlayClassName,
            animation = 'default',
            ...props
        },
        ref
    ) => {
        const content = (
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    'fixed left-[50%] top-[50%] z-50 grid w-full translate-y-[-50%] gap-4 border bg-background-900 p-6',
                    'shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ',
                    'data-[state=open]:fade-in-0 sm:rounded-lg',
                    animation === 'default' &&
                        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
                    {
                        'translate-x-[-50%]': !fullScreen,
                        'translate-x-[calc(-50%-0.5rem)] max-h-modalH': fullScreen,
                        'max-w-modal': fullScreen && !manualMaxWidth,
                        'max-w-lg': !fullScreen && !manualMaxWidth,
                    },
                    theme === 'dark' && 'bg-neutral-950 border-accent-foreground',
                    className
                )}
                {...props}
            >
                {children}
                {!noClose && (
                    <DialogPrimitive.Close
                        className={cn(
                            'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent-foreground data-[state=open]:text-muted-foreground'
                        )}
                    >
                        <X className={cn('h-4 w-4  text-neutral-900', theme === 'dark' && 'text-neutral-200')} />
                        <span className="sr-only text-neutral-900">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        );

        return (
            <DialogPortal>
                <DialogOverlay className={overlayClassName}>{content}</DialogOverlay>
            </DialogPortal>
        );
    }
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
